import { SheetClose, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useFormFieldVisibility } from "@/context/FormFieldVisibilityContext";
import { cn } from "@/lib/utils";
import { Cross1Icon } from "@radix-ui/react-icons";
import { ChevronDownIcon, Download, Eye, EyeOff, Trash2Icon } from "lucide-react";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { BadgeCell } from "../../table/CustomCell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "../../ui/menubar";

type SculptureEditionSheetHeaderProps = {
  isReader: boolean;
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (open: boolean) => void;
  formattedSheetTitle: string;
  publicationStatus?: string;
  onDelete: () => void;
  onSaveDraft: () => void;
  onSavePublished: () => void;
  t: (key: string) => string;
  /** Extra items appended to the "Actions" menu (e.g. visibility submenus). */
  actionsMenuExtra?: ReactNode;
};

function FieldVisibilityMenuItem() {
  const { t } = useTranslation();
  const { allFieldsMinimized, minimizeAllFields, expandAllFields } =
    useFormFieldVisibility();

  return (
    <MenubarItem
      onClick={() => (allFieldsMinimized ? expandAllFields() : minimizeAllFields())}
      className="gap-2"
    >
      {allFieldsMinimized ? (
        <Eye className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      ) : (
        <EyeOff className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      )}
      {allFieldsMinimized ? t("formField.showAll") : t("formField.hideAll")}
    </MenubarItem>
  );
}

const SculptureEditionSheetHeader = ({
  isReader,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  formattedSheetTitle,
  publicationStatus,
  onDelete,
  onSaveDraft,
  onSavePublished,
  t,
  actionsMenuExtra,
}: SculptureEditionSheetHeaderProps) => {
  const vt = useVisibleT();
  const status = publicationStatus ?? "draft";
  return (
    <SheetHeader className="p-8 pt-4 pb-0 gap-2 flex-shrink-0 flex flex-row items-start justify-between">
      <div className="flex items-start gap-2 min-w-[70%]">
        <div className="flex items-center gap-2 mt-0.5">
          <SheetClose>
            <Cross1Icon className="h-4 w-4 relative text-gray-400" />
          </SheetClose>
          <BadgeCell
            value={t(status)}
            variant={status === "published" ? "published" : "draft"}
          />
        </div>
        <SheetTitle className="w-full">{formattedSheetTitle}</SheetTitle>
      </div>
      <div className="flex items-start justify-end gap-2 !m-0 w-full">
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("confirmDeleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("confirmDeleteDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {!isReader && (
          <Menubar className="border-none">
            <MenubarMenu>
              <MenubarTrigger className="px-3 py-2 rounded-lg justify-center items-center gap-2 flex border border-input bg-white hover:bg-slate-100 whitespace-nowrap min-w-fit">
                <p className="text-sm font-semibold font-['Raleway']">
                  {t("actions")}
                </p>
                <ChevronDownIcon className="w-5 h-5" />
              </MenubarTrigger>
              <MenubarContent>
                <MenubarItem disabled className="gap-2">
                  <Download className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
                  {t("singleArtworkPrintBtn")}
                </MenubarItem>
                <FieldVisibilityMenuItem />
                {actionsMenuExtra}
                <MenubarSeparator />
                <MenubarItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="gap-2 text-red-500"
                >
                  <Trash2Icon className="h-4 w-4 shrink-0" aria-hidden />
                  {t("delete")}
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        )}

        <Menubar className="border-none">
          <MenubarMenu>
            <MenubarTrigger
              className={cn(
                "px-3 py-2 rounded-lg justify-center items-center gap-2 flex bg-primary text-white",
                isReader && "opacity-50 cursor-default",
              )}
              disabled={isReader}
            >
              <p className="text-sm font-semibold font-['Raleway']">
                {vt("bo.form.save", "Enregistrer")}
              </p>
              <ChevronDownIcon className="w-5 h-5 " />
            </MenubarTrigger>
            <MenubarContent>
              <MenubarItem onClick={onSaveDraft}>
                {t("sculpture.form.saveAsDraft")}
              </MenubarItem>
              <MenubarItem onClick={onSavePublished}>
                {t("sculpture.form.saveAndPublish")}
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      </div>
    </SheetHeader>
  );
};

export default SculptureEditionSheetHeader;
